import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { BrowserProvider, Contract } from "ethers";

// Helper function: returns post.id if available, otherwise post._id.
const getPostId = (post) => post.id || post._id;

// Set your contract address and ABI.
const CONTRACT_ADDRESS = "0x1c713297e32c91694135d552422d6c9f914bdae6";
const CONTRACT_ABI = [
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: "uint256", name: "nftId", type: "uint256" },
      { indexed: true, internalType: "address", name: "minter", type: "address" },
      { indexed: false, internalType: "uint256", name: "amountPaid", type: "uint256" }
    ],
    name: "MintedToCastle",
    type: "event"
  },
  {
    inputs: [
      { internalType: "uint256", name: "nftId", type: "uint256" },
      { internalType: "uint256", name: "price", type: "uint256" }
    ],
    name: "mintCastle",
    outputs: [],
    stateMutability: "payable",
    type: "function"
  },
  {
    inputs: [{ internalType: "uint256", name: "", type: "uint256" }],
    name: "mintedCount",
    outputs: [{ internalType: "uint256", name: "count", type: "uint256" }],
    stateMutability: "view",
    type: "function"
  }
];

// Connect to MetaMask using ethers v6.
async function connectWallet() {
  if (!window.ethereum) {
    alert("MetaMask is not installed. Please install MetaMask. -bamn ::E");
    throw new Error("No wallet provider found");
  }

  await window.ethereum.request({ method: "eth_requestAccounts" });
  const provider = new BrowserProvider(window.ethereum);
  const signer = await provider.getSigner();
  const userAddress = await signer.getAddress();

  return { signer, userAddress };
}

// Instantiate the contract with the signer.
async function getContract(signer) {
  return new Contract(CONTRACT_ADDRESS, CONTRACT_ABI, signer);
}

export default function CryptPage() {
  const navigate = useNavigate();
  const { walletAddress } = useParams();

  const [libraryNFTs, setLibraryNFTs] = useState([]);
  const [currentUser, setCurrentUser] = useState("");
  const [searchInput, setSearchInput] = useState("");

  const fetchUserLibrary = async (address) => {
    try {
      if (!address) return;

      const backendUrl = process.env.REACT_APP_BACKEND_API_URL;
      if (!backendUrl) {
        throw new Error("REACT_APP_BACKEND_API_URL is not defined");
      }

      const response = await fetch(`${backendUrl}/api/crypt/${address}`);
      if (!response.ok) {
        throw new Error("Failed to fetch Crypt NFTs");
      }

      const data = await response.json();
      setLibraryNFTs(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("❌ Error fetching Crypt NFTs:", error, "-bamn ::E");
      setLibraryNFTs([]);
    }
  };

  const handleConnectWallet = async () => {
    try {
      const { signer, userAddress } = await connectWallet();
      await getContract(signer); // keeps getContract actively used

      setCurrentUser(userAddress);

      if (!walletAddress) {
        fetchUserLibrary(userAddress);
      }
    } catch (error) {
      console.error("Could not connect wallet:", error);
    }
  };

  useEffect(() => {
    if (walletAddress) {
      fetchUserLibrary(walletAddress);
    } else if (currentUser) {
      fetchUserLibrary(currentUser);
    }
  }, [walletAddress, currentUser]);

  const handleSearch = (e) => {
    e.preventDefault();

    const trimmed = searchInput.trim();
    if (!trimmed) return;

    navigate(`/crypt/${trimmed}`);
    setSearchInput("");
  };

  return (
    <div className="min-h-screen bg-brandBlack text-brandWhite p-6">
      <h1 className="text-4xl font-gothic text-center mb-6">CRYPT</h1>

      {!currentUser ? (
        <div className="text-center mb-6">
          <p>Connect your wallet to view your Crypt NFTs.</p>
          <button
            type="button"
            onClick={handleConnectWallet}
            className="mt-4 p-2 bg-gray-700 text-white rounded"
          >
            Connect Wallet
          </button>
        </div>
      ) : (
        <p className="text-center mb-6 text-gray-400">
          Viewing {walletAddress ? walletAddress : "your"} Crypt
        </p>
      )}

      <form onSubmit={handleSearch} className="flex justify-center mb-6">
        <input
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Enter wallet address..."
          className="w-1/2 p-2 bg-gray-800 text-white rounded-l"
        />
        <button
          type="submit"
          className="bg-gray-700 text-white px-4 rounded-r"
        >
          View
        </button>
      </form>

      {libraryNFTs.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {libraryNFTs.map((nft) => {
            const nftId = getPostId(nft);

            return (
              <div
                key={nftId}
                className="relative border border-gray-700 rounded p-2"
              >
                {nft.type === "writtenRecord" ? (
                  <div className="w-full h-40 flex items-center justify-center bg-gray-800 text-white p-4">
                    <p>{nft.description || "Written Record NFT"}</p>
                  </div>
                ) : (
                  <img
                    src={nft.url || nft.image}
                    alt={nft.title || "NFT"}
                    className="w-full h-auto"
                  />
                )}

                <div className="mt-2">
                  <h2 className="text-lg">
                    {nft.title ||
                      (nft.type === "writtenRecord"
                        ? "Written Record"
                        : "Untitled NFT")}
                  </h2>
                  <p className="text-gray-400 text-sm">
                    Owner: {nft.owner || "Unknown"}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-center text-gray-400">
          No NFTs found in this Crypt.
        </p>
      )}
    </div>
  );
}